import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  Heading,
  PinInput,
  PinInputField,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react";
import { config } from "../runtimeConfig";

const RAW_CODE_PATTERN = /^[A-Z0-9]{8}$/;
const INVALID_CODE_MESSAGE =
  "Invalid or expired code. Please check your device and try again.";
const NETWORK_ERROR_MESSAGE =
  "Unable to validate code right now. Please check your connection and try again.";

// Localhost (NOT a deployed env) fallbacks for the rare case where runtime
// config is entirely absent — matching the build-time defaults in fe/.env.
// A baked deployed-env URL here would be a SILENT fallback to the wrong
// environment if a deployed env's `fe-runtime-config` ConfigMap were missing;
// localhost makes that obviously-broken instead. In practice the deployed
// container always supplies real URLs via window._env_, so these never fire.
const DEFAULT_VALIDATE_BASE_URL = "http://localhost:8000/api/v1";
const DEFAULT_KEYCLOAK_DEVICE_URL =
  "http://localhost:8080/realms/ethosphere/device";

const normalizeRawCode = (rawValue) =>
  rawValue
    .replace(/[^a-zA-Z0-9]/g, "")
    .toUpperCase()
    .slice(0, 8);

const toFormattedCode = (rawCode) =>
  `${rawCode.slice(0, 4)}-${rawCode.slice(4, 8)}`;

const getValidateBaseUrl = () => {
  const envBase = config.BACKEND_URL || "";
  const envApiPrefix = config.API || "";
  const combined = `${envBase}${envApiPrefix}`.trim();

  return (combined || DEFAULT_VALIDATE_BASE_URL).replace(/\/$/, "");
};

const getKeycloakDeviceBaseUrl = () => {
  const keycloakUrl = config.KEYCLOAK_URL;
  const keycloakRealm = config.KEYCLOAK_REALM;

  if (keycloakUrl && keycloakRealm) {
    return `${keycloakUrl.replace(/\/$/, "")}/realms/${keycloakRealm}/device`;
  }

  return DEFAULT_KEYCLOAK_DEVICE_URL;
};

function DeviceLoginPage() {
  const [rawCode, setRawCode] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const codeFromQuery = new URLSearchParams(window.location.search).get("code");
    if (codeFromQuery) {
      setRawCode(normalizeRawCode(codeFromQuery.trim()));
    }
  }, []);

  const isCodeValid = RAW_CODE_PATTERN.test(rawCode);

  const handleCodeChange = (value) => {
    setRawCode(normalizeRawCode(value));
    setErrorMessage("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const normalizedRawCode = normalizeRawCode(rawCode.trim());
    if (!RAW_CODE_PATTERN.test(normalizedRawCode)) {
      setErrorMessage("Please enter a valid code in XXXX-XXXX format.");
      return;
    }

    const normalizedCode = toFormattedCode(normalizedRawCode);

    setIsSubmitting(true);
    setErrorMessage("");

    const validateUrl = `${getValidateBaseUrl()}/device/validate?user_code=${encodeURIComponent(
      normalizedCode
    )}`;

    try {
      const response = await fetch(validateUrl, {
        method: "GET",
        headers: {
          Accept: "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`Validation request failed: ${response.status}`);
      }

      const result = await response.json();
      if (!result.valid) {
        setErrorMessage(INVALID_CODE_MESSAGE);
        return;
      }

      const redirectUrl = `${getKeycloakDeviceBaseUrl()}?user_code=${encodeURIComponent(
        normalizedCode
      )}`;
      window.location.href = redirectUrl;
    } catch (error) {
      setErrorMessage(NETWORK_ERROR_MESSAGE);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box
      minHeight="100vh"
      display="flex"
      alignItems="center"
      justifyContent="center"
      bg="gray.50"
      px={4}
    >
      <Box
        w="full"
        maxW="460px"
        bg="white"
        borderRadius="lg"
        p={8}
        boxShadow="md"
      >
        <VStack align="stretch" spacing={5}>
          <Heading size="md">Enter Device Code</Heading>
          <Text color="gray.600" fontSize="sm">
            Enter the code shown on your microphone to continue.
          </Text>

          <form onSubmit={handleSubmit}>
            <VStack align="stretch" spacing={4}>
              <FormControl isInvalid={Boolean(errorMessage)}>
                <Box display="flex" justifyContent="center" alignItems="center" gap={1.5}>
                  <PinInput
                    value={rawCode}
                    onChange={handleCodeChange}
                    type="alphanumeric"
                    otp
                    isDisabled={isSubmitting}
                    autoFocus
                  >
                    <PinInputField textTransform="uppercase" w="40px" h="50px" fontSize="lg" textAlign="center" />
                    <PinInputField textTransform="uppercase" w="40px" h="50px" fontSize="lg" textAlign="center" />
                    <PinInputField textTransform="uppercase" w="40px" h="50px" fontSize="lg" textAlign="center" />
                    <PinInputField textTransform="uppercase" w="40px" h="50px" fontSize="lg" textAlign="center" />
                  </PinInput>
                  <Text color="gray.400" fontWeight="bold" fontSize="xl" lineHeight={1} userSelect="none" mx={1}>—</Text>
                  <PinInput
                    value={rawCode.slice(4)}
                    onChange={(v) => handleCodeChange(rawCode.slice(0, 4) + v)}
                    type="alphanumeric"
                    otp
                    isDisabled={isSubmitting}
                  >
                    <PinInputField textTransform="uppercase" w="40px" h="50px" fontSize="lg" textAlign="center" />
                    <PinInputField textTransform="uppercase" w="40px" h="50px" fontSize="lg" textAlign="center" />
                    <PinInputField textTransform="uppercase" w="40px" h="50px" fontSize="lg" textAlign="center" />
                    <PinInputField textTransform="uppercase" w="40px" h="50px" fontSize="lg" textAlign="center" />
                  </PinInput>
                </Box>
                {errorMessage ? (
                  <FormErrorMessage>{errorMessage}</FormErrorMessage>
                ) : null}
              </FormControl>

              <Button
                colorScheme="blue"
                type="submit"
                isDisabled={!isCodeValid || isSubmitting}
              >
                {isSubmitting ? <Spinner size="sm" /> : "Log In"}
              </Button>
            </VStack>
          </form>
        </VStack>
      </Box>
    </Box>
  );
}

export default DeviceLoginPage;
